<template>
  <transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-class="opacity-0"
      enter-to-class="opacity-100"
      leave-class="opacity-100"
      leave-to-class="opacity-0"
  >
    <div
        v-if="orderSuccess && orderSuccess.visible"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
        dir="rtl"
    >
      <div class="w-full max-w-lg bg-slate-950/95 rounded-3xl border border-white/10 shadow-2xl overflow-hidden">
        <div class="p-6 border-b border-white/10 bg-gradient-to-b from-slate-900/70 to-transparent">
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
              <div class="flex items-center gap-2 flex-wrap mb-2">
                <h3 class="text-lg font-bold text-slate-50">
                  {{ successHeader.title }}
                </h3>
              </div>

              <span class="flex-shrink-0 text-xs text-slate-400 mb-1">
                کد پیگیری:
                <span class="text-sm text-slate-200">
                  {{ successOrder && successOrder.id ? successOrder.id : '---' }}
                </span>
              </span>

              <span class="text-xs text-slate-400 mb-1 mr-5">
                تاریخ سفارش:
                <span class="text-sm text-slate-200">
                  {{ successOrder && successOrder.created_at ? successOrder.created_at : '---' }}
                </span>
              </span>
            </div>

            <div class="flex-shrink-0">
              <div
                  class="text-center p-3 rounded-xl bg-slate-900/60 border border-white/10 min-w-[120px] items-center text-center">
                <div class="text-xs text-slate-400 mb-2 text-center">وضعیت فعلی</div>
                <div
                    class="text-lg font-bold text-center"
                    :class="getStatusColor(successOrder.status)"
                >
                  {{ getStatusText(successOrder.status) }}
                </div>

                <div
                    v-if="hasReturns"
                    class="mt-2 inline-flex items-center justify-center px-2 py-1 rounded-lg border border-orange-400/40 bg-orange-500/15 text-orange-200 text-[10px] font-black"
                >
                  دارای مرجوعی
                  <span v-if="returnsCount" class="mr-1">
    / {{ returnsCount }}
  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-6 p-4 rounded-2xl bg-gradient-to-r from-slate-900/60 to-slate-800/40 border border-white/10">
            <div class="flex items-center justify-between mb-4">
              <div>
                <div class="text-sm font-semibold text-slate-100">تغییر وضعیت سفارش</div>
              </div>

              <div
                  class="hidden md:flex items-center gap-2 text-xs"
                  v-if="successOrder.status === 'dr' || successOrder.status === 'cf'"
              >
                <div class="flex items-center gap-1">
                  <div
                      class="w-2 h-2 rounded-full"
                      :class="successOrder.status === 'dr' ? 'bg-amber-500' : 'bg-slate-500'"
                  ></div>
                  <span :class="successOrder.status === 'dr' ? 'text-slate-300' : 'text-slate-100/50'">
                    پیش‌نویس
                  </span>
                </div>

                <div class="text-slate-600">←</div>

                <div class="flex items-center gap-1">
                  <div
                      class="w-2 h-2 rounded-full"
                      :class="successOrder.status === 'cf' ? 'bg-green-500' : 'bg-slate-500'"
                  ></div>
                  <span :class="successOrder.status === 'cf' ? 'text-slate-300' : 'text-slate-100/50'">
                    نهایی
                  </span>
                </div>
              </div>
            </div>

            <template v-if="successOrder.status === 'dr'">
              <div class="mb-4 p-3 rounded-md bg-slate-800/30 border border-white/5">
                <div class="flex items-center gap-2 mb-3">
                  <div
                      class="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center"
                      style="max-width: 30px"
                  >
                    <svg class="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>

                  <div>
                    <div class="text-sm font-medium text-slate-100">سفارش در وضعیت پیش‌نویس</div>
                    <div class="text-xs text-slate-400">لطفاً وضعیت را نهایی کنید یا در صورت نیاز لغو نمایید</div>
                  </div>
                </div>

                <div class="flex flex-wrap gap-3">
                  <button
                      type="button"
                      @click="$emit('apply-success-status', 'ca')"
                      :disabled="successStatusLoading"
                      class="px-5 py-2.5 rounded-md items-center text-sm font-semibold bg-rose-500/20 text-rose-200
                         border border-rose-400/30 hover:bg-rose-500/30 hover:border-rose-400/50
                         active:scale-[0.98] transition-all duration-200 disabled:opacity-60
                         disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                    لغو سفارش
                  </button>

                  <button
                      type="button"
                      @click="$emit('apply-success-status', 'cf')"
                      :disabled="successStatusLoading"
                      class="px-5 py-2.5 rounded-md items-center text-sm font-semibold bg-gradient-to-r from-emerald-500 to-teal-600
                        text-slate-950 hover:shadow-lg hover:shadow-emerald-500/30 active:scale-[0.98]
                        transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
                        flex items-center gap-2 min-w-[120px] justify-center"
                  >
                    <svg
                        v-if="successStatusLoading"
                        class="w-4 h-4 animate-spin"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>

                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                    </svg>

                    {{ successStatusLoading ? 'در حال انجام...' : 'نهایی کردن سفارش' }}
                  </button>
                </div>
              </div>
            </template>

            <template v-else-if="successOrder.status === 'cf'">
              <div class="mb-4 p-3 rounded-xl bg-slate-800/30 border border-amber-500/20">
                <div class="flex items-center gap-2 mb-3">
                  <div class="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <svg class="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                  </div>

                  <div>
                    <div class="text-sm font-medium text-slate-100">سفارش تأیید شده</div>
                    <div class="text-xs text-slate-400">در صورت مرجوعی خریدار، وضعیت را تغییر دهید</div>
                  </div>
                </div>

                <div class="flex flex-wrap gap-3">
                  <button
                      type="button"
                      @click="$emit('open-return')"
                      :disabled="successStatusLoading"
                      class="px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-orange-700 to-orange-600
       text-slate-950 hover:shadow-lg hover:shadow-orange-500/30 active:scale-[0.98]
       transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
       flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                    </svg>
                    ثبت بازگشت از خرید
                  </button>
                  <button
                      type="button"
                      @click="$emit('apply-success-status', 're')"
                      :disabled="successStatusLoading || hasReturnDocument(successOrder)"
                      class="px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-amber-500 to-orange-600
                         text-slate-950 hover:shadow-lg hover:shadow-amber-500/30 active:scale-[0.98]
                         transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed
                         flex items-center gap-2"
                  >
                    <svg
                        v-if="successStatusLoading"
                        class="w-4 h-4 animate-spin"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>

                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                    </svg>

                    {{ successStatusLoading ? 'در حال انجام...' : 'برگشت کامل مستقیم' }}
                  </button>
                  <p
                      v-if="hasReturnDocument(successOrder)"
                      class="mt-3 text-[11px] leading-6 text-rose-200 bg-rose-500/10 border border-rose-400/20 rounded-xl px-3 py-2"
                  >
                    برای این فاکتور سند مرجوعی ثبت شده است؛ بعد از مرجوعی سندی، برگشت کامل مستقیم وضعیت مجاز نیست.
                  </p>
                </div>
              </div>
            </template>

            <template v-if="successOrder.status === 're' || successOrder.status === 'ca'">
              <div
                  class="p-4 rounded-xl bg-slate-800/40 border"
                  :class="successOrder.status === 'ca' ? 'border-rose-500/20' : 'border-indigo-500/20'"
              >
                <div class="flex items-center gap-3">
                  <div
                      class="w-10 h-10 rounded-md flex items-center justify-center"
                      style="max-width: 50px"
                      :class="successOrder.status === 'ca' ? 'bg-rose-500/20' : 'bg-indigo-500/20'"
                  >
                    <svg
                        class="w-5 h-5"
                        :class="successOrder.status === 'ca' ? 'text-rose-400' : 'text-indigo-400'"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                      <path
                          v-if="successOrder.status === 'ca'"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                      <path
                          v-else
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z"
                      />
                    </svg>
                  </div>

                  <div class="flex-1">
                    <div
                        class="text-sm font-medium"
                        :class="successOrder.status === 'ca' ? 'text-rose-200' : 'text-indigo-200'"
                    >
                      {{ successOrder.status === 'ca' ? 'سفارش لغو شده' : 'سفارش برگشت خورده ' }}
                    </div>

                    <div
                        class="text-xs mt-1"
                        :class="successOrder.status === 'ca' ? 'text-rose-300/70' : 'text-indigo-300/70'"
                    >
                      {{ lockedStatusMessage }}
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <div
                v-if="successStatusError"
                class="mt-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20"
            >
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>

                <div class="text-xs text-rose-300">{{ successStatusError }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="p-6 space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div class="rounded-2xl bg-slate-900/60 border border-white/10 p-4">
              <div class="text-[11px] text-slate-400 mb-2">اطلاعات مشتری</div>

              <div class="text-sm font-semibold text-slate-100 truncate">
                {{ successCustomerName || '---' }}
              </div>

              <div class="mt-1 text-xs text-slate-300">
                {{ successCustomerPhone || '---' }}
              </div>
            </div>

            <div class="rounded-2xl bg-slate-900/60 border border-white/10 p-4">
              <div class="text-[11px] text-slate-400 mb-2">خلاصه پرداخت</div>

              <div class="flex justify-between text-xs">
                <span class="text-slate-400">جمع ردیف‌ها</span>
                <span class="text-slate-100 font-medium">
                  {{ (successOrder.total_items_price || 0) | toMoney(0) }}
                </span>
              </div>

              <div class="flex justify-between text-xs mt-1">
                <span class="text-slate-400">تخفیف ردیف‌ها</span>
                <span class="text-emerald-300 font-medium">
                  {{ (successOrder.total_line_discount || 0) | toMoney(0) }}
                </span>
              </div>

              <div class="flex justify-between text-xs mt-1">
                <span class="text-slate-400">تخفیف کلی</span>
                <span class="text-emerald-300 font-medium">
                  {{ (successOrder.total_order_discount || 0) | toMoney(0) }}
                </span>
              </div>

              <div
                  v-if="hasReturns"
                  class="flex justify-between text-xs mt-1"
              >
                <span class="text-slate-400">مبلغ فاکتور</span>
                <span class="text-slate-400 line-through font-medium">
    {{ (successOrder.final_amount || grandTotal || 0) | toMoney(0) }}
  </span>
              </div>

              <div
                  v-if="hasReturns"
                  class="flex justify-between text-xs mt-1"
              >
                <span class="text-slate-400">مبلغ مرجوعی</span>
                <span class="text-rose-300 font-bold">
    -{{ totalReturnedAmount | toMoney(0) }}
  </span>
              </div>

              <div class="flex justify-between mt-2 pt-2 border-t border-white/10">
  <span class="text-slate-200 font-semibold text-sm">
    {{ hasReturns ? 'خالص قابل پرداخت' : 'مبلغ نهایی' }}
  </span>

                <span
                    class="font-bold text-base"
                    :class="hasReturns ? 'text-cyan-300' : 'text-emerald-300'"
                >
    {{ displayFinalAmount | toMoney(0) }}
  </span>
              </div>            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
            <button
                type="button"
                @click="$emit('open-order-details', successOrder)"
                class="text-center items-center px-4 py-3 rounded-2xl bg-slate-900 text-slate-100 hover:bg-slate-800 border border-white/10 transition-all duration-200 text-sm font-semibold"
            >
              مشاهده جزئیات سفارش
            </button>

            <button
                type="button"
                @click="$emit('print-from-success')"
                class="text-center items-center px-4 py-3 rounded-2xl bg-indigo-500 text-white hover:bg-indigo-400 transition-all duration-200 text-sm font-semibold"
            >
              {{ successPrintLabel }}
            </button>

            <button
                type="button"
                @click="$emit('copy-order-link', successOrder)"
                class="text-center items-center px-4 py-3 rounded-2xl bg-slate-800 text-slate-100 hover:bg-slate-700 border border-white/10 transition-all duration-200 text-sm"
            >
              کپی لینک فاکتور
            </button>

            <button
                type="button"
                @click="$emit('close')"
                class="text-center items-center px-4 py-3 rounded-2xl border border-white/10 text-slate-100 hover:bg-slate-900 transition-all duration-200 text-sm"
            >
              بستن و ثبت سفارش جدید
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'PosOrderSuccessModal',
  computed: {
    hasReturns() {
      const o = this.successOrder || {}

      return (
          Number(o.total_returned_amount || 0) > 0 ||
          Number(o.returns_count || 0) > 0
      )
    },

    displayFinalAmount() {
      const o = this.successOrder || {}

      if (
          o.net_amount_after_returns !== undefined &&
          o.net_amount_after_returns !== null &&
          o.net_amount_after_returns !== ''
      ) {
        return Number(o.net_amount_after_returns || 0)
      }

      return Number(o.final_amount || this.grandTotal || 0)
    },

    totalReturnedAmount() {
      const o = this.successOrder || {}
      return Number(o.total_returned_amount || 0)
    },

    returnsCount() {
      const o = this.successOrder || {}
      return Number(o.returns_count || 0)
    }
  },
  props: {
    successKeyboardAction: {
      type: String,
      default: 'primary'
    },
    orderSuccess: {
      type: Object,
      required: true
    },
    successHeader: {
      type: Object,
      required: true
    },
    successOrder: {
      type: Object,
      default: () => ({})
    },
    successStatusLoading: {
      type: Boolean,
      default: false
    },
    successStatusError: {
      type: String,
      default: ''
    },
    successCustomerName: {
      type: String,
      default: ''
    },
    successCustomerPhone: {
      type: String,
      default: ''
    },
    successPrintLabel: {
      type: String,
      default: 'چاپ فاکتور'
    },
    lockedStatusMessage: {
      type: String,
      default: ''
    },
    grandTotal: {
      type: Number,
      default: 0
    },
    getStatusColor: {
      type: Function,
      required: true
    },
    getStatusText: {
      type: Function,
      required: true
    }
  },
  methods: {
    hasReturnDocument(order) {
      if (!order) return false

      return (
          Number(order.returns_count || 0) > 0 ||
          (Array.isArray(order.returns) && order.returns.length > 0)
      )
    },
    money(value) {
      const num = Number(value || 0)
      if (Number.isNaN(num)) return value || '0'
      return num.toLocaleString('fa-IR') + ' تومان'
    },

    isSelectedAction(action) {
      return this.successKeyboardAction === action
    },

    actionClass(action, activeClass) {
      return this.isSelectedAction(action)
          ? `${activeClass || ''} ring-2 ring-white/40 scale-[1.02]`
          : ''
    },

    returnRows() {
      const o = this.successOrder || {}
      return Array.isArray(o.returns) ? o.returns : []
    }
  }
}
</script>